import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  CaretRightOutlined,
  CaretDownOutlined,
  PlusOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { LayerTypeIcon } from "../../icons-and-animations/Icons";
import { useMst } from "../../stores/main";
import {
  formatTextAnimationPresetName,
  TEXT_ANIMATION_PHASES,
  TREE_ROW_HEIGHT,
} from "./utils/keyframeHelpers";
import type { TextAnimationPhase } from "./utils/keyframeHelpers";
import {
  buildTimelinePresetTiming,
  buildTimelineMotionConfig,
  normalizeTimelineMotionValue,
  TIMELINE_ANIMATABLE_LABELS,
  TIMELINE_ANIMATABLE_PROPERTIES,
} from "./utils/animationPresetHelpers";
import type {
  TimelineAnimatableProperty,
  TimelineMotionPhase,
} from "./utils/animationPresetHelpers";
import type { TimelineLayerNode } from "./utils/layerHierarchy";
import { getLayerData } from "./utils/layerHierarchy";
import { getResolvedLayerTiming } from "./TimelineTrack";
import { applyCustomModsToLayerData } from "./utils/moddedLayerData";
import { useAnimationJsonEdit } from "./hooks/useAnimationJsonEdit";
import { isUndoRedoShortcut } from "../../hooks/keyboardShortcutGuards";
import type { ModConnection } from "./TimelineLayerLabel.types";
import {
  TEXT_ANIMATION_PHASE_LABELS,
  TEXT_STYLE_ANIMATION_CONFIGS,
  LAYER_STYLE_MENU_LABELS,
  buildTextPresetMenuKey,
  parseTextPresetMenuKey,
  buildTextStyleMenuKey,
  parseTextStyleMenuKey,
  buildLayerStyleMenuKey,
  parseLayerStyleMenuKey,
  getTimelineMotionTarget,
  supportsTimelineAnimation,
  collectLayerStyleAnimationTargets,
  buildLayerStyleAnimationTiming,
  buildLayerStyleTargetValue,
  ensureSolidFillColorEffect,
  parseHexColor,
  getResolvedPropertyValueAtFrame,
  hasAnimatedTextStyleProperty,
  ensureTextStyleAnimatorProperty,
  getTextStyleBaseValue,
  getTextStyleTargetValue,
  buildTextAnimationSelectorTiming,
} from "./utils/timelineLayerAnimationUtils";
import {
  MotionPresetGallery,
  buildMotionPresetGalleryItems,
} from "./MotionPresetGallery";
import type {
  LayerStyleAnimationProperty,
  LayerStyleAnimationTarget,
  TextStyleAnimationProperty,
  TimelineMotionPresetDirection,
} from "./utils/timelineLayerAnimationUtils";

type DragHandleProps = React.HTMLAttributes<HTMLButtonElement> & {
  "data-drag-enabled"?: boolean;
};

const TEXT_STYLE_MENU_GROUPS: Array<{
  key: string;
  label: string;
  propertyKeys: TextStyleAnimationProperty[];
}> = [
  { key: "text-style-hue", label: "Hue", propertyKeys: ["fh", "sh"] },
  {
    key: "text-style-saturation",
    label: "Saturation",
    propertyKeys: ["fs", "ss"],
  },
  {
    key: "text-style-brightness",
    label: "Brightness",
    propertyKeys: ["fb", "sb"],
  },
  {
    key: "text-style-colors",
    label: "Colors",
    propertyKeys: ["fc", "fo", "sc", "sw", "so"],
  },
];

type LayerNodeLabelProps = {
  node: TimelineLayerNode;
  isPrecompExpanded?: (nodeKey: string) => boolean;
  isSelected: boolean;
  isHovered: boolean;
  isRowHovered: boolean;
  connections: ModConnection[];
  dragHandleProps?: DragHandleProps;
  dragDisabledReason?: string;
  isDropContainer?: boolean;
  isDropInside?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export const LayerNodeLabel = observer(
  ({
    node,
    isSelected,
    isHovered,
    isRowHovered,
    connections,
    dragHandleProps,
    dragDisabledReason,
    isDropContainer,
    isDropInside,
    isPrecompExpanded,
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave,
  }: LayerNodeLabelProps) => {
    const {
      timelineStore,
      playerStore: { jsonFile, currentFrame },
      modsStore,
    } = useMst();
    const { applyAnimationJsonEdit } = useAnimationJsonEdit();
    const { layer, depth, hasChildren, hasKeyframes, keyframesExpanded } = node;
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [isEditingLayerName, setIsEditingLayerName] = useState(false);
    const [layerNameDraft, setLayerNameDraft] = useState(layer.nm);
    const layerNameInputRef = useRef<HTMLInputElement>(null);
    const skipLayerNameBlurCommitRef = useRef(false);
    const modsRevision = modsStore.trigger;
    const {
      "data-drag-enabled": dragEnabled,
      onClick: onDragHandleClick,
      onMouseDown: onDragHandleMouseDown,
      onKeyDown: onDragHandleKeyDown,
      ...dragHandleRestProps
    } = dragHandleProps ?? {};

    const isExpanded = isPrecompExpanded
      ? isPrecompExpanded(layer.nodeKey)
      : timelineStore.isPrecompExpanded(layer.nodeKey);
    const mods = useMemo(() => {
      void modsRevision;
      return modsStore.mods?.slice() ?? [];
    }, [modsRevision, modsStore.mods]);
    const motionTarget = useMemo(() => getTimelineMotionTarget(layer), [layer]);
    const canAddTimelineAnimation = supportsTimelineAnimation(layer.ty);
    const layerTiming = useMemo(() => getResolvedLayerTiming(layer, mods), [
      layer,
      mods,
    ]);
    const boundedCurrentFrame = Math.max(
      layerTiming.ip,
      Math.min(layerTiming.op, currentFrame)
    );
    const resolvedLayerData = useMemo(() => {
      if (!jsonFile) {
        return undefined;
      }
      const layerData = getLayerData(jsonFile, layer.layerPath);
      return applyCustomModsToLayerData(layerData, layer, mods);
    }, [jsonFile, layer, mods]);
    const layerStyleTargets = useMemo(
      () =>
        resolvedLayerData
          ? collectLayerStyleAnimationTargets(resolvedLayerData)
          : new Map<
              Exclude<LayerStyleAnimationProperty, "blur">,
              LayerStyleAnimationTarget[]
            >(),
      [resolvedLayerData]
    );

    useEffect(() => {
      if (!isEditingLayerName) {
        setLayerNameDraft(layer.nm);
      }
    }, [isEditingLayerName, layer.nm]);

    useEffect(() => {
      if (!isEditingLayerName) {
        return;
      }

      layerNameInputRef.current?.focus();
      layerNameInputRef.current?.select();
    }, [isEditingLayerName]);

    const cancelLayerNameEdit = useCallback(() => {
      skipLayerNameBlurCommitRef.current = true;
      setLayerNameDraft(layer.nm);
      setIsEditingLayerName(false);
    }, [layer.nm]);

    const commitLayerNameEdit = useCallback(() => {
      const nextName = layerNameDraft.trim();
      if (!nextName || nextName === layer.nm) {
        cancelLayerNameEdit();
        return;
      }

      const result = applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.setPropertyValue(
            workingAnimation as any,
            motionTarget,
            "layerName",
            nextName
          );

          if (applied.changes.length) {
            modsStore.renameLayerConnectorTargets(motionTarget, {
              ...motionTarget,
              layerName: nextName,
            });
          }

          return applied.changes.length ? applied : false;
        },
        {
          playerUpdate: {
            immediate: true,
            startFrame: currentFrame,
          },
        }
      );

      if (result === false) {
        console.warn("[Timeline layer name] failed to rename layer", {
          layerName: layer.nm,
          nextName,
          target: motionTarget,
        });
        return;
      }

      skipLayerNameBlurCommitRef.current = true;
      setIsEditingLayerName(false);
    }, [
      applyAnimationJsonEdit,
      cancelLayerNameEdit,
      currentFrame,
      layer.nm,
      layerNameDraft,
      motionTarget,
      modsStore,
    ]);

    const startLayerNameEdit = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        skipLayerNameBlurCommitRef.current = false;
        setLayerNameDraft(layer.nm);
        setIsEditingLayerName(true);
      },
      [layer.nm]
    );

    const openConnectedMod = useCallback(
      (event: React.MouseEvent | React.KeyboardEvent, modId: number) => {
        event.preventDefault();
        event.stopPropagation();
        modsStore.setActiveMod(modId);
      },
      [modsStore]
    );

    const handleConnectedModHover = useCallback(
      (modId?: number) => {
        modsStore.setHoveredControlMod(modId);
      },
      [modsStore]
    );
    const singleConnectedModId =
      connections.length === 1 ? connections[0].modId : undefined;
    const handleLayerNameDoubleClick = useCallback(
      (event: React.MouseEvent) => {
        if (typeof singleConnectedModId === "number") {
          openConnectedMod(event, singleConnectedModId);
          return;
        }

        startLayerNameEdit(event);
      },
      [openConnectedMod, singleConnectedModId, startLayerNameEdit]
    );

    const handleLayerNameInputBlur = useCallback(() => {
      if (skipLayerNameBlurCommitRef.current) {
        skipLayerNameBlurCommitRef.current = false;
        return;
      }

      commitLayerNameEdit();
    }, [commitLayerNameEdit]);

    const handleLayerNameInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();

        if (event.key === "Enter") {
          event.preventDefault();
          commitLayerNameEdit();
        } else if (event.key === "Escape") {
          event.preventDefault();
          cancelLayerNameEdit();
        }
      },
      [cancelLayerNameEdit, commitLayerNameEdit]
    );

    const finishAddAnimation = useCallback(() => {
      if (!timelineStore.isLayerKeyframesExpanded(layer.nodeKey)) {
        timelineStore.toggleLayerKeyframes(layer.nodeKey);
      }
      setIsAddMenuOpen(false);
    }, [layer.nodeKey, timelineStore]);

    const handleAddLayerPreset = useCallback(
      (
        presetName: string,
        phase: TimelineMotionPhase,
        direction?: TimelineMotionPresetDirection
      ) => {
        if (!jsonFile) {
          return;
        }

        applyAnimationJsonEdit((workingAnimation) => {
          const timing = buildTimelinePresetTiming({
            phase,
            ip: layerTiming.ip,
            op: layerTiming.op,
            timelineOffset: layerTiming.timelineOffset,
            frameRate: (workingAnimation as { fr?: number }).fr,
          });
          const applied = motionAPI.presets.applyPreset(
            workingAnimation as any,
            motionTarget,
            presetName as Parameters<typeof motionAPI.presets.applyPreset>[2],
            {
              timing,
              phase,
              ...(direction ? { direction } : {}),
            }
          );

          if (!applied.changes.length) {
            return false;
          }

          finishAddAnimation();
          return applied;
        });
      },
      [
        applyAnimationJsonEdit,
        finishAddAnimation,
        jsonFile,
        layerTiming,
        motionTarget,
      ]
    );

    const motionPresetItems = useMemo(
      () => buildMotionPresetGalleryItems(motionAPI.presets.listPresets()),
      []
    );

    const addAnimationItems = useMemo<MenuProps["items"]>(() => {
      if (!canAddTimelineAnimation) {
        return [];
      }

      const transformItems = TIMELINE_ANIMATABLE_PROPERTIES.map((property) => {
        const animationSummary = jsonFile
          ? motionAPI.inspect.getPropertyAnimation(
              jsonFile as any,
              motionTarget,
              property
            )
          : undefined;
        const disabled = animationSummary?.isAnimated === true;

        return {
          key: property,
          label: TIMELINE_ANIMATABLE_LABELS[property],
          disabled,
        };
      });
      const items: NonNullable<MenuProps["items"]> = [...transformItems];

      const blurAnimationSummary = jsonFile
        ? motionAPI.inspect.getPropertyAnimation(
            jsonFile as any,
            motionTarget,
            "blur"
          )
        : undefined;
      const getLayerStyleTargets = (
        propertyKey: Exclude<LayerStyleAnimationProperty, "blur">
      ) => layerStyleTargets.get(propertyKey) ?? [];
      const getAvailableLayerStyleTargets = (
        propertyKey: Exclude<LayerStyleAnimationProperty, "blur">
      ) =>
        getLayerStyleTargets(propertyKey).filter(
          (target) =>
            target.kind !== "existing" ||
            !jsonFile ||
            motionAPI.inspect.getPropertyAnimation(
              jsonFile as any,
              motionTarget,
              target.path
            )?.isAnimated !== true
        );

      const layerStyleChildren: NonNullable<MenuProps["items"]> = [
        {
          key: buildLayerStyleMenuKey("blur"),
          label: LAYER_STYLE_MENU_LABELS.blur,
          disabled: blurAnimationSummary?.isAnimated === true,
        },
      ];

      (["hue", "saturation", "brightness"] as const).forEach((propertyKey) => {
        if (!getLayerStyleTargets(propertyKey).length) {
          return;
        }

        layerStyleChildren.push({
          key: buildLayerStyleMenuKey(propertyKey),
          label: LAYER_STYLE_MENU_LABELS[propertyKey],
          disabled: getAvailableLayerStyleTargets(propertyKey).length === 0,
        });
      });

      const colorMenuItems = ([
        "fillColor",
        "fillOpacity",
        "strokeColor",
        "strokeWidth",
        "strokeOpacity",
      ] as const)
        .filter((propertyKey) => getLayerStyleTargets(propertyKey).length > 0)
        .map((propertyKey) => ({
          key: buildLayerStyleMenuKey(propertyKey),
          label: LAYER_STYLE_MENU_LABELS[propertyKey],
          disabled: getAvailableLayerStyleTargets(propertyKey).length === 0,
        }));

      if (colorMenuItems.length) {
        layerStyleChildren.push({
          key: "layer-style-colors",
          label: "Colors",
          children: colorMenuItems,
        });
      }

      items.push(
        {
          type: "divider",
        },
        {
          key: "layer-style",
          label: "Layer styles",
          children: layerStyleChildren,
        }
      );

      if (layer.ty === 5) {
        items.push(
          {
            type: "divider",
          },
          {
            key: "text-style",
            label: "Text styles",
            children: TEXT_STYLE_MENU_GROUPS.map((group) => ({
              key: group.key,
              label: group.label,
              children: group.propertyKeys.map((propertyKey) => ({
                key: buildTextStyleMenuKey(propertyKey),
                label: TEXT_STYLE_ANIMATION_CONFIGS[propertyKey].label,
                disabled: hasAnimatedTextStyleProperty(
                  resolvedLayerData,
                  propertyKey
                ),
              })),
            })),
          }
        );
      }

      items.push(
        {
          type: "divider",
        },
        {
          key: "motion-presets",
          label: "Motion presets",
          popupClassName:
            "timeline-layer-add-dropdown timeline-motion-preset-submenu",
          children: [
            {
              key: "motion-presets-gallery",
              className: "timeline-motion-preset-gallery-menu-item",
              label: (
                <MotionPresetGallery
                  items={motionPresetItems}
                  onSelect={handleAddLayerPreset}
                />
              ),
            },
          ],
        }
      );

      if (layer.ty !== 5) {
        return items;
      }

      items.push(
        {
          type: "divider",
        },
        {
          key: "text-presets",
          label: "Character animation",
          children: motionAPI.textPresets
            .listTextPresets()
            .map((presetName) => ({
              key: presetName,
              label: formatTextAnimationPresetName(presetName),
              children: TEXT_ANIMATION_PHASES.map((phase) => ({
                key: buildTextPresetMenuKey(presetName, phase),
                label: TEXT_ANIMATION_PHASE_LABELS[phase],
              })),
            })),
        }
      );

      return items;
    }, [
      canAddTimelineAnimation,
      jsonFile,
      layer.ty,
      layerStyleTargets,
      handleAddLayerPreset,
      motionTarget,
      motionPresetItems,
      resolvedLayerData,
    ]);

    const handleToggleExpand = useCallback(
      (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        timelineStore.togglePrecompExpansion(layer.nodeKey);
      },
      [layer.nodeKey, timelineStore]
    );

    const handleToggleKeyframes = useCallback(
      (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        timelineStore.toggleLayerKeyframes(layer.nodeKey);
      },
      [layer.nodeKey, timelineStore]
    );

    const handleAddAnimation = useCallback(
      (property: TimelineAnimatableProperty) => {
        if (!jsonFile) {
          return;
        }

        const animationSummary = motionAPI.inspect.getPropertyAnimation(
          jsonFile as any,
          motionTarget,
          property
        );

        const motionConfig = buildTimelineMotionConfig({
          property,
          currentValue: normalizeTimelineMotionValue(
            animationSummary?.value,
            property
          ),
          currentFrame: boundedCurrentFrame,
          ip: layerTiming.ip,
          op: layerTiming.op,
          timelineOffset: layerTiming.timelineOffset,
        });

        if (!motionConfig) {
          return;
        }

        applyAnimationJsonEdit((workingAnimation) => {
          const applied = motionAPI.apply.applyPropertyAnimation(
            workingAnimation as any,
            motionTarget,
            motionConfig
          );

          if (!applied.changes.length) {
            return false;
          }

          finishAddAnimation();
          return applied;
        });
      },
      [
        applyAnimationJsonEdit,
        boundedCurrentFrame,
        finishAddAnimation,
        jsonFile,
        layerTiming,
        motionTarget,
      ]
    );

    const handleAddLayerStyleAnimation = useCallback(
      (propertyKey: LayerStyleAnimationProperty) => {
        if (!jsonFile) {
          return;
        }

        const timingConfig = buildLayerStyleAnimationTiming({
          currentFrame,
          layerTiming,
        });
        if (!timingConfig) {
          return;
        }

        if (propertyKey === "blur") {
          applyAnimationJsonEdit((workingAnimation) => {
            const applied = motionAPI.apply.applyPropertyAnimation(
              workingAnimation as any,
              motionTarget,
              {
                property: "blur",
                timing: timingConfig.timing,
                phase: timingConfig.phase,
                baseValueSource: "valueAtStart",
                deltaValue: 12,
              }
            );

            if (!applied.changes.length) {
              return false;
            }

            finishAddAnimation();
            return applied;
          });
          return;
        }

        const availableTargets = (
          layerStyleTargets.get(propertyKey) ?? []
        ).filter(
          (target) =>
            target.kind !== "existing" ||
            motionAPI.inspect.getPropertyAnimation(
              jsonFile as any,
              motionTarget,
              target.path
            )?.isAnimated !== true
        );
        if (!availableTargets.length) {
          return;
        }

        applyAnimationJsonEdit((workingAnimation) => {
          const resolvedTarget = motionAPI.inspect.resolveTarget(
            workingAnimation as any,
            motionTarget
          )[0];
          if (!resolvedTarget) {
            return false;
          }

          const localCurrentFrame =
            boundedCurrentFrame - layerTiming.timelineOffset;
          let hasChanges = false;

          availableTargets.forEach((target) => {
            const path =
              target.kind === "existing"
                ? target.path
                : ensureSolidFillColorEffect(resolvedTarget.layer)?.path;
            if (!path) {
              return;
            }

            const baseValue =
              getResolvedPropertyValueAtFrame(
                resolvedTarget.layer,
                path,
                localCurrentFrame
              ) ??
              (target.kind === "solidFillColor"
                ? parseHexColor(target.color)
                : undefined);
            const valueConfig = buildLayerStyleTargetValue(
              propertyKey,
              baseValue
            );
            const applied = motionAPI.apply.applyPropertyAnimation(
              workingAnimation as any,
              motionTarget,
              {
                property: path,
                timing: timingConfig.timing,
                phase: timingConfig.phase,
                baseValueSource: "valueAtStart",
                ...(valueConfig.toValue !== undefined
                  ? { toValue: valueConfig.toValue as any }
                  : {}),
                ...(valueConfig.deltaValue !== undefined
                  ? { deltaValue: valueConfig.deltaValue as any }
                  : {}),
              }
            );

            if (applied.changes.length) {
              hasChanges = true;
            }
          });

          if (!hasChanges) {
            return false;
          }

          finishAddAnimation();
          return true;
        });
      },
      [
        applyAnimationJsonEdit,
        boundedCurrentFrame,
        currentFrame,
        finishAddAnimation,
        jsonFile,
        layerStyleTargets,
        layerTiming,
        motionTarget,
      ]
    );

    const handleAddTextStyleAnimation = useCallback(
      (propertyKey: TextStyleAnimationProperty) => {
        if (!jsonFile || layer.ty !== 5) {
          return;
        }

        const timingConfig = buildLayerStyleAnimationTiming({
          currentFrame,
          layerTiming,
        });
        if (!timingConfig) {
          return;
        }

        const config = TEXT_STYLE_ANIMATION_CONFIGS[propertyKey];

        applyAnimationJsonEdit((workingAnimation) => {
          const resolvedTarget = motionAPI.inspect.resolveTarget(
            workingAnimation as any,
            motionTarget
          )[0];
          if (!resolvedTarget) {
            return false;
          }

          const localCurrentFrame =
            boundedCurrentFrame - layerTiming.timelineOffset;
          const baseValue = getTextStyleBaseValue(
            resolvedTarget.layer,
            propertyKey,
            localCurrentFrame
          );
          const propertyPath = ensureTextStyleAnimatorProperty({
            layer: resolvedTarget.layer,
            propertyKey,
            animatorName: config.animatorName,
            baseValue,
          });
          if (!propertyPath) {
            return false;
          }

          const applied = motionAPI.apply.applyPropertyAnimation(
            workingAnimation as any,
            motionTarget,
            {
              property: propertyPath,
              timing: timingConfig.timing,
              phase: timingConfig.phase,
              toValue: getTextStyleTargetValue(propertyKey, baseValue) as any,
            }
          );

          if (!applied.changes.length) {
            return false;
          }

          finishAddAnimation();
          return applied;
        });
      },
      [
        applyAnimationJsonEdit,
        boundedCurrentFrame,
        currentFrame,
        finishAddAnimation,
        jsonFile,
        layer.ty,
        layerTiming,
        motionTarget,
      ]
    );

    const handleAddTextPreset = useCallback(
      (presetName: string, phase: TextAnimationPhase) => {
        if (!jsonFile) {
          return;
        }

        const timing = buildTextAnimationSelectorTiming({
          phase,
          existingTiming: {},
          layerTiming,
        });

        applyAnimationJsonEdit((workingAnimation) => {
          const applied = motionAPI.textPresets.applyTextPreset(
            workingAnimation as any,
            motionTarget,
            presetName as Parameters<
              typeof motionAPI.textPresets.applyTextPreset
            >[2],
            {
              timing,
              phase,
              grouping: "characters",
              staggerFrames: 2,
            }
          );

          if (!applied.changes.length) {
            return false;
          }

          finishAddAnimation();
          return applied;
        });
      },
      [
        applyAnimationJsonEdit,
        finishAddAnimation,
        jsonFile,
        layerTiming,
        motionTarget,
      ]
    );

    const classNames = [
      "timeline-tree-node",
      "timeline-tree-node--layer",
      isSelected && "timeline-tree-node--selected",
      isHovered && "timeline-tree-node--hovered",
      isRowHovered && "timeline-tree-node--row-hovered",
      isDropContainer && "timeline-tree-node--drop-container",
      isDropInside && "timeline-tree-node--drop-inside",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={classNames}
        data-node-key={node.nodeKey}
        style={{ height: TREE_ROW_HEIGHT }}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick((e as unknown) as React.MouseEvent);
          }
        }}
        onContextMenu={onContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {dragHandleProps || dragDisabledReason ? (
          <button
            {...dragHandleRestProps}
            type="button"
            className={`timeline-tree-node__drag-handle ${
              dragEnabled
                ? "timeline-tree-node__drag-handle--active"
                : "timeline-tree-node__drag-handle--disabled"
            }`}
            title={dragDisabledReason ?? "Drag to reorder layer"}
            aria-label={`Reorder ${layer.nm}`}
            disabled={!dragEnabled}
            onClick={(e) => {
              onDragHandleClick?.(e);
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              onDragHandleMouseDown?.(e);
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (isUndoRedoShortcut(e)) {
                return;
              }

              onDragHandleKeyDown?.(e);
              e.stopPropagation();
            }}
          >
            <HolderOutlined />
          </button>
        ) : null}

        <div
          className="timeline-tree-node__indent"
          style={{ width: depth * 12 }}
        />

        {hasChildren ? (
          <div
            className={`timeline-tree-node__toggle ${
              isExpanded ? "timeline-tree-node__toggle--expanded" : ""
            }`}
            role="button"
            tabIndex={0}
            onClick={handleToggleExpand}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleExpand(e);
              }
            }}
          >
            {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
          </div>
        ) : (
          <div className="timeline-tree-node__toggle-spacer" />
        )}

        {layer.isPrecomp && typeof layer.childCount === "number" && (
          <span className="timeline-tree-node__count">{layer.childCount}</span>
        )}

        {hasKeyframes ? (
          <div
            className={`timeline-tree-node__keyframe-toggle ${
              keyframesExpanded
                ? "timeline-tree-node__keyframe-toggle--expanded"
                : ""
            }`}
            role="button"
            tabIndex={0}
            onClick={handleToggleKeyframes}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleKeyframes(e);
              }
            }}
          >
            <HoverTooltip
              title={keyframesExpanded ? "Hide Keyframes" : "Show Keyframes"}
              description={
                keyframesExpanded
                  ? "Collapse the animated property rows for this layer."
                  : "Show this layer's animated properties and keyframes."
              }
              placement="right"
            >
              <span className="timeline-tree-node__tooltip-icon">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="5"
                    y="0.5"
                    width="6.36"
                    height="6.36"
                    rx="1"
                    transform="rotate(45 5 0.5)"
                    fill={keyframesExpanded ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                </svg>
              </span>
            </HoverTooltip>
          </div>
        ) : (
          <div className="timeline-tree-node__keyframe-toggle-spacer" />
        )}

        <div className="timeline-tree-node__icon">
          <LayerTypeIcon layerType={layer.ty} size={14} />
        </div>

        {isEditingLayerName ? (
          <input
            ref={layerNameInputRef}
            className="timeline-tree-node__name-input"
            value={layerNameDraft}
            aria-label="Layer name"
            onChange={(event) => setLayerNameDraft(event.target.value)}
            onBlur={handleLayerNameInputBlur}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onKeyDown={handleLayerNameInputKeyDown}
          />
        ) : (
          <span
            className="timeline-tree-node__name"
            title={layer.nm}
            onDoubleClick={handleLayerNameDoubleClick}
          >
            {layer.nm}
          </span>
        )}

        {connections.length > 0 && (
          <div className="timeline-tree-node__indicators">
            {connections.map((conn, i) => (
              <button
                type="button"
                key={`${conn.modId}-${i}`}
                className="timeline-tree-node__indicator"
                style={{ backgroundColor: conn.color }}
                title={`Connected to mod ${conn.modId}`}
                aria-label={`Open connector ${conn.modId}`}
                onMouseDown={(event) => event.stopPropagation()}
                onMouseEnter={() => handleConnectedModHover(conn.modId)}
                onMouseLeave={() => handleConnectedModHover()}
                onClick={(event) => openConnectedMod(event, conn.modId)}
                onDoubleClick={(event) => openConnectedMod(event, conn.modId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    openConnectedMod(event, conn.modId);
                  }
                }}
              />
            ))}
          </div>
        )}

        {canAddTimelineAnimation ? (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="timeline-layer-add-dropdown"
            open={isAddMenuOpen}
            onOpenChange={setIsAddMenuOpen}
            menu={{
              items: addAnimationItems,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (typeof key === "string") {
                  const parsedTextPreset = parseTextPresetMenuKey(key);
                  if (parsedTextPreset) {
                    handleAddTextPreset(
                      parsedTextPreset.presetName,
                      parsedTextPreset.phase
                    );
                    return;
                  }
                  const parsedTextStyle = parseTextStyleMenuKey(key);
                  if (parsedTextStyle) {
                    handleAddTextStyleAnimation(parsedTextStyle);
                    return;
                  }
                  const parsedLayerStyle = parseLayerStyleMenuKey(key);
                  if (parsedLayerStyle) {
                    handleAddLayerStyleAnimation(parsedLayerStyle);
                    return;
                  }
                }
                if (
                  TIMELINE_ANIMATABLE_PROPERTIES.includes(
                    key as TimelineAnimatableProperty
                  )
                ) {
                  handleAddAnimation(key as TimelineAnimatableProperty);
                }
              },
            }}
          >
            <button
              type="button"
              className={`timeline-tree-node__add-action ${
                isRowHovered || isAddMenuOpen
                  ? "timeline-tree-node__add-action--visible"
                  : ""
              }`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Add timeline animation"
            >
              <HoverTooltip
                title="Add Animation"
                description="Add a motion or style animation to this layer."
                placement="right"
              >
                <span className="timeline-tree-node__tooltip-icon">
                  <PlusOutlined />
                </span>
              </HoverTooltip>
            </button>
          </Dropdown>
        ) : null}
      </div>
    );
  }
);
